import type { RecurringTemplate } from '@/types/recurring';
import { addTransaction } from '@/lib/firestore';
import { updateLastProcessedDate } from '@/lib/firestore-recurring';

// ─── Date helpers ────────────────────────────────────────────────────────────

/** Strip time component so comparisons are day-accurate regardless of timezone */
function toMidnight(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isSameWeek(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    getWeekNumber(a) === getWeekNumber(b)
  );
}

function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function isSameYear(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear();
}

function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

// ─── Core scheduling logic ────────────────────────────────────────────────────

/**
 * Returns every date from startDate up to (and including) targetDate
 * on which this template should fire — and hasn't been processed yet.
 *
 * This is the key fix: instead of asking "should today fire?" we ask
 * "what are ALL the dates since we last ran that we missed?"
 */
export function getMissedOccurrences(
  template: RecurringTemplate,
  targetDate: Date = new Date()
): Date[] {
  if (!template.isActive) return [];

  const start = toMidnight(template.startDate);
  const end = template.endDate ? toMidnight(template.endDate) : null;
  const today = toMidnight(targetDate);

  // Don't process anything beyond today or end date
  const ceiling = end && end < today ? end : today;

  if (start > ceiling) return [];

  const lastProcessed = template.lastProcessedDate
    ? toMidnight(template.lastProcessedDate)
    : null;

  const occurrences: Date[] = [];

  switch (template.frequency) {
    case 'daily': {
      // Walk every day from start (or day after last processed) to ceiling
      const from = lastProcessed
        ? new Date(lastProcessed.getTime() + 86400000) // next day
        : start;
      let cursor = new Date(from);
      while (cursor <= ceiling) {
        occurrences.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
      break;
    }

    case 'weekly': {
      const targetDay = template.dayOfWeek; // 0-6

      // Find first occurrence >= start date
      let cursor = new Date(start);
      if (targetDay !== undefined) {
        // Advance to the correct day of week
        const diff = (targetDay - cursor.getDay() + 7) % 7;
        cursor.setDate(cursor.getDate() + diff);
      }

      while (cursor <= ceiling) {
        // Skip if already processed this week
        if (!lastProcessed || !isSameWeek(cursor, lastProcessed)) {
          if (cursor >= start && cursor <= ceiling) {
            occurrences.push(new Date(cursor));
          }
        }
        cursor.setDate(cursor.getDate() + 7);
      }
      break;
    }

    case 'monthly': {
      const targetDay = template.dayOfMonth;

      // Iterate month by month from the start month to the current month
      let cursorYear = start.getFullYear();
      let cursorMonth = start.getMonth();
      const ceilYear = ceiling.getFullYear();
      const ceilMonth = ceiling.getMonth();

      while (
        cursorYear < ceilYear ||
        (cursorYear === ceilYear && cursorMonth <= ceilMonth)
      ) {
        // Find the actual day to use (handle months with fewer days)
        const lastDayOfMonth = new Date(cursorYear, cursorMonth + 1, 0).getDate();
        const day =
          targetDay !== undefined
            ? Math.min(targetDay, lastDayOfMonth)
            : start.getDate();

        const occurrence = new Date(cursorYear, cursorMonth, day);

        // Must be >= start, <= ceiling, and not already processed this month
        if (
          occurrence >= start &&
          occurrence <= ceiling &&
          (!lastProcessed || !isSameMonth(occurrence, lastProcessed))
        ) {
          occurrences.push(occurrence);
        }

        // Advance to next month
        cursorMonth++;
        if (cursorMonth > 11) {
          cursorMonth = 0;
          cursorYear++;
        }
      }
      break;
    }

    case 'yearly': {
      const targetMonth = template.monthOfYear ? template.monthOfYear - 1 : start.getMonth();
      const targetDay = template.dayOfMonth ?? start.getDate();

      for (let year = start.getFullYear(); year <= ceiling.getFullYear(); year++) {
        const lastDayOfMonth = new Date(year, targetMonth + 1, 0).getDate();
        const day = Math.min(targetDay, lastDayOfMonth);
        const occurrence = new Date(year, targetMonth, day);

        if (
          occurrence >= start &&
          occurrence <= ceiling &&
          (!lastProcessed || !isSameYear(occurrence, lastProcessed))
        ) {
          occurrences.push(occurrence);
        }
      }
      break;
    }
  }

  return occurrences;
}

// ─── Processing ───────────────────────────────────────────────────────────────

/**
 * Process a single template: creates ALL missed transactions up to today.
 * Returns how many were created.
 */
export async function processRecurringTemplate(
  template: RecurringTemplate,
  targetDate: Date = new Date()
): Promise<number> {
  const missed = getMissedOccurrences(template, targetDate);

  if (missed.length === 0) return 0;

  console.log(
    `📅 ${template.name}: ${missed.length} missed occurrence(s) to create`
  );

  let created = 0;

  for (const date of missed) {
    try {
      const year = date.getFullYear();
      const monthId = `${year}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      await addTransaction(template.userId, year, monthId, {
        name: template.name,
        amount: template.amount,
        type: template.type,
        categoryId: template.categoryId,
        date,
        isRecurring: true,
        recurringTemplateId: template.id,
      });

      // Update lastProcessedDate after each successful write
      // This way if we crash mid-loop, we only redo what's truly missing
      await updateLastProcessedDate(template.id, date);

      created++;
      console.log(`✅ Created ${template.name} for ${date.toLocaleDateString()}`);
    } catch (error) {
      console.error(`❌ Failed to create ${template.name} for ${date.toLocaleDateString()}:`, error);
      // Don't break — try remaining dates
    }
  }

  return created;
}

/**
 * Process all active recurring templates for a user.
 */
export async function processAllRecurringTemplates(
  templates: RecurringTemplate[],
  targetDate: Date = new Date()
): Promise<{ processed: number; failed: number }> {
  let processed = 0;
  let failed = 0;

  console.log(`🔄 Processing ${templates.length} recurring template(s)...`);

  for (const template of templates) {
    try {
      const count = await processRecurringTemplate(template, targetDate);
      processed += count;
    } catch (error) {
      console.error(`❌ Error processing template ${template.id}:`, error);
      failed++;
    }
  }

  console.log(`✅ Created ${processed} transaction(s), ${failed} template(s) failed`);
  return { processed, failed };
}

// ─── Display helpers ──────────────────────────────────────────────────────────

/**
 * Returns the next N future occurrence dates for a template.
 * Always looks forward from today, never from lastProcessedDate.
 */
export function getUpcomingOccurrences(
  template: RecurringTemplate,
  count: number = 5
): Date[] {
  const today = toMidnight(new Date());
  const occurrences: Date[] = [];

  // Simulate future dates by calling getMissedOccurrences with future ceilings
  // Strategy: step forward one period at a time until we have `count` dates
  let cursor = new Date(today);
  cursor.setDate(cursor.getDate() + 1); // Start from tomorrow

  // Lookahead window: enough to cover `count` occurrences for any frequency
  const lookaheadDays =
    template.frequency === 'daily'
      ? count + 1
      : template.frequency === 'weekly'
        ? (count + 1) * 7
        : template.frequency === 'monthly'
          ? (count + 2) * 32
          : (count + 2) * 370;

  const horizon = new Date(today);
  horizon.setDate(horizon.getDate() + lookaheadDays);

  // Build a fake template with lastProcessedDate = today to get future ones
  const fakeTemplate: RecurringTemplate = {
    ...template,
    lastProcessedDate: today,
  };

  const future = getMissedOccurrences(fakeTemplate, horizon);
  return future.slice(0, count);
}

// ─── Format frequency for display ────────────────────────────────────────────

export function formatFrequency(
  template: RecurringTemplate,
  language: 'en' | 'fr' = 'en'
): string {
  const { frequency, dayOfWeek, dayOfMonth, monthOfYear } = template;

  const dayNames = {
    en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    fr: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
  };
  const monthNames = {
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
  };

  const labels = {
    en: {
      daily: 'Daily',
      weekly: 'Weekly',
      weeklyOn: 'Weekly on',
      monthly: 'Monthly',
      monthlyOn: 'Monthly on the',
      yearly: 'Yearly',
      yearlyIn: 'in',
      yearlyOn: 'on the',
    },
    fr: {
      daily: 'Quotidien',
      weekly: 'Hebdomadaire',
      weeklyOn: 'Chaque semaine le',
      monthly: 'Mensuel',
      monthlyOn: 'Chaque mois le',
      yearly: 'Annuel',
      yearlyIn: 'en',
      yearlyOn: 'le',
    },
  };

  const l = labels[language];

  switch (frequency) {
    case 'daily':
      return l.daily;

    case 'weekly':
      return dayOfWeek !== undefined
        ? `${l.weeklyOn} ${dayNames[language][dayOfWeek]}`
        : l.weekly;

    case 'monthly': {
      if (dayOfMonth !== undefined) {
        const suffix = language === 'fr' ? '' : getOrdinalSuffix(dayOfMonth);
        return `${l.monthlyOn} ${dayOfMonth}${suffix}`;
      }
      return l.monthly;
    }

    case 'yearly': {
      let str = l.yearly;
      if (monthOfYear !== undefined) {
        str += ` ${l.yearlyIn} ${monthNames[language][monthOfYear - 1]}`;
      }
      if (dayOfMonth !== undefined) {
        const suffix = language === 'fr' ? '' : getOrdinalSuffix(dayOfMonth);
        str += ` ${l.yearlyOn} ${dayOfMonth}${suffix}`;
      }
      return str;
    }

    default:
      return frequency;
  }
}

function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}
